// src/pages/Privacy.jsx
import React, { useEffect } from "react";

/**
 * Chesshood — Privacy Policy page (sole trader / individual)
 * Plain React (JS), no TypeScript.
 * Includes: SEO <title>/<meta>, JSON-LD, GDPR/UK GDPR, AdMob, RevenueCat/Stripe, etc.
 * Replace constants below with your real info.
 */

const CONTROLLER_NAME = "Lukasz Piecha"; // your legal name (person)
const TRADING_NAME = "Chesshood"; // your public/brand name
const CONTACT_EMAIL = "chesshood@chesshood.com"; // your contact email
const CONTROLLER_ADDRESS = "Winchester, United Kingdom"; // optional
const SITE_URL = "https://chesshood.com";
const LAST_UPDATED = "October 6, 2025";

function Privacy() {
  useEffect(() => {
    // Basic SEO (if you don't use react-helmet / Next Head)
    document.title = `${TRADING_NAME} – Privacy Policy`;
    const content =
      "Privacy Policy for Chesshood (mobile apps and website): data we collect, how we use it, ads, subscriptions, your rights (GDPR/UK GDPR), and contact information.";
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute("content", content);

    // JSON-LD (WebPage + Person controller)
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Privacy Policy",
      url: `${SITE_URL}/privacy`,
      lastReviewed: LAST_UPDATED,
      about: {
        "@type": "Person",
        name: CONTROLLER_NAME,
        address: CONTROLLER_ADDRESS,
      },
      isPartOf: {
        "@type": "WebSite",
        name: TRADING_NAME,
        url: SITE_URL,
      },
      brand: {
        "@type": "Organization",
        name: TRADING_NAME,
        url: SITE_URL,
      },
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <main className="mx-auto max-w-3xl px-4 py-10 text-slate-800 textprivacy">
      <header className="mb-8">
        <h1 className="text-3xl font-bold">Privacy Policy</h1>
        <p className="mt-2 text-sm text-slate-500">
          Last updated: {LAST_UPDATED}
        </p>
        <p className="mt-4">
          This Privacy Policy explains how {TRADING_NAME} (“we”, “us”, “our”)
          collects, uses, and shares information when you use our mobile
          applications and website at{"  "}
          <a
            href={SITE_URL}
            className="underline decoration-dotted"
            target="_blank"
            rel="noreferrer"
          >
            {SITE_URL}
          </a>
          .
        </p>
        <p className="mt-2 text-sm">
          Data Controller: {CONTROLLER_NAME}
          {CONTROLLER_ADDRESS ? ` — ${CONTROLLER_ADDRESS}` : ""}. Contact:{" "}
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="underline decoration-dotted"
          >
            {CONTACT_EMAIL}
          </a>
          .
        </p>
      </header>

      {/* Table of Contents */}
      <nav
        aria-label="Table of contents"
        className="mb-10 rounded-2xl border border-slate-200 p-4"
      >
        <h2 className="text-lg font-semibold">Contents</h2>
        <ul className="mt-3 space-y-2 list-disc pl-6">
          {[
            ["scope", "1. Scope"],
            ["data-we-collect", "2. Data we collect"],
            ["how-we-use", "3. How we use data"],
            ["legal-bases", "4. Legal bases (GDPR/UK GDPR)"],
            ["sharing", "5. Sharing & processors"],
            ["ads", "6. Advertising (AdMob) & consent"],
            ["subscriptions", "7. Subscriptions & payments"],
            ["retention", "8. Data retention"],
            ["children", "9. Children’s privacy"],
            ["security", "10. Security"],
            ["transfers", "11. International transfers"],
            ["your-rights", "12. Your rights"],
            ["requests", "13. How to make a request"],
            ["third-party", "14. Third-party links"],
            ["changes", "15. Changes to this policy"],
            ["contact", "16. Contact & Controller"],
            ["terms", "17. Terms of Use (EULA)"],
          ].map(([id, label]) => (
            <li key={id}>
              <a className="underline decoration-dotted" href={`#${id}`}>
                {label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <section id="scope" className="prose prose-slate max-w-none">
        <h2>1. Scope</h2>
        <p>
          This policy applies to our mobile apps (iOS/Android) and our website,
          including any related customer support and communications.
        </p>
      </section>

      <section id="data-we-collect" className="prose prose-slate max-w-none">
        <h2>2. Data we collect</h2>
        <ul>
          <li>
            <strong>Account &amp; Authentication</strong> (e.g., email, display
            name, hashed IDs) via providers such as Firebase Authentication.
          </li>
          <li>
            <strong>Profile &amp; Usage</strong> (training categories, puzzles
            attempted, results, ratings, app settings, OS/app version).
          </li>
          <li>
            <strong>Purchases &amp; Subscriptions</strong> (product, region,
            status) from App Store / Google Play, aggregated by RevenueCat; for
            web, via Stripe.
          </li>
          <li>
            <strong>Diagnostics</strong> (crash logs, performance metrics).
          </li>
          <li>
            <strong>Advertising identifiers</strong> if ads are shown (e.g.,
            Google AdMob).
          </li>
        </ul>
        <p>
          We do not knowingly collect special category data unless you
          explicitly provide it.
        </p>
      </section>

      <section id="how-we-use" className="prose prose-slate max-w-none">
        <h2>3. How we use data</h2>
        <ul>
          <li>Provide, maintain, and improve our services.</li>
          <li>
            Personalize training, select puzzles, track progress and ratings.
          </li>
          <li>Process purchases, subscriptions, and account changes.</li>
          <li>Show ads (where applicable) and measure their performance.</li>
          <li>Prevent abuse and ensure security.</li>
          <li>Comply with legal obligations and enforce our Terms.</li>
        </ul>
      </section>

      <section id="legal-bases" className="prose prose-slate max-w-none">
        <h2>4. Legal bases (GDPR/UK GDPR)</h2>
        <ul>
          <li>
            <strong>Contract</strong> — to provide requested services.
          </li>
          <li>
            <strong>Legitimate interests</strong> — to secure and improve
            services, measure performance.
          </li>
          <li>
            <strong>Consent</strong> — for optional personalized ads/analytics
            (where required).
          </li>
          <li>
            <strong>Legal obligation</strong> — to comply with applicable laws.
          </li>
        </ul>
      </section>

      <section id="sharing" className="prose prose-slate max-w-none">
        <h2>5. Sharing &amp; processors</h2>
        <p>We use trusted providers under data protection terms:</p>
        <ul>
          <li>
            <strong>Firebase</strong> — authentication and related services.
          </li>
          <li>
            <strong>Azure Functions / Hosting</strong> — backend processing and
            APIs.
          </li>
          <li>
            <strong>MongoDB Atlas</strong> — application database (e.g.,
            puzzles, history).
          </li>
          <li>
            <strong>RevenueCat</strong> — subscription status from App Store /
            Google Play.
          </li>
          <li>
            <strong>Stripe</strong> (web) — payment processing on the web.
          </li>
          <li>
            <strong>Google AdMob</strong> (if used) — ad delivery and
            measurement.
          </li>
        </ul>
        <p>
          We do not sell personal data. We may disclose information if required
          by law.
        </p>
      </section>

      <section id="ads" className="prose prose-slate max-w-none">
        <h2>6. Advertising (AdMob) &amp; consent</h2>
        <p>
          Where we show ads, we may request your consent (and provide controls)
          for personalized advertising in regions where required. You can update
          your choices in the app settings or your device settings.
        </p>
      </section>

      <section id="subscriptions" className="prose prose-slate max-w-none">
        <h2>7. Subscriptions &amp; payments</h2>
        <p>
          In-app subscriptions are billed by Apple (App Store) or Google (Google
          Play). We receive non-sensitive subscription status and entitlement
          data via RevenueCat. For web payments, we may use Stripe. We do not
          receive or store full payment card data on our servers.
        </p>
      </section>

      <section id="retention" className="prose prose-slate max-w-none">
        <h2>8. Data retention</h2>
        <p>
          We retain data for as long as necessary to provide the service, comply
          with legal obligations, resolve disputes, and enforce agreements. We
          periodically review and anonymize or delete data that is no longer
          needed.
        </p>
      </section>

      <section id="children" className="prose prose-slate max-w-none">
        <h2>9. Children’s privacy</h2>
        <p>
          Our services may be used by young players under the guidance of
          parents/guardians where applicable. If you believe a child provided
          personal data without consent, contact us to remove it.
        </p>
      </section>

      <section id="security" className="prose prose-slate max-w-none">
        <h2>10. Security</h2>
        <p>
          We implement reasonable technical and organizational measures to
          protect information. However, no method of transmission or storage is
          100% secure.
        </p>
      </section>

      <section id="transfers" className="prose prose-slate max-w-none">
        <h2>11. International transfers</h2>
        <p>
          We may process data outside your country (e.g., EEA/UK to US). Where
          required, we rely on appropriate safeguards such as Standard
          Contractual Clauses.
        </p>
      </section>

      <section id="your-rights" className="prose prose-slate max-w-none">
        <h2>12. Your rights</h2>
        <ul>
          <li>Access, rectification, erasure, restriction.</li>
          <li>Portability and objection (including to profiling for ads).</li>
          <li>
            Withdraw consent at any time (does not affect past lawful
            processing).
          </li>
          <li>
            Complain to your data protection authority (e.g., ICO in the UK).
          </li>
        </ul>
      </section>

      <section id="requests" className="prose prose-slate max-w-none">
        <h2>13. How to make a request</h2>
        <p>
          To exercise your rights, contact us at{" "}
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="underline decoration-dotted"
          >
            {CONTACT_EMAIL}
          </a>
          . We may ask you to verify your identity.
        </p>
      </section>

      <section id="third-party" className="prose prose-slate max-w-none">
        <h2>14. Third-party links</h2>
        <p>
          Our services may contain links to third-party sites. Their privacy
          practices are governed by their own policies.
        </p>
      </section>

      <section id="changes" className="prose prose-slate max-w-none">
        <h2>15. Changes to this policy</h2>
        <p>
          We may update this policy from time to time. The updated version will
          be posted on this page with a new “Last updated” date.
        </p>
      </section>

      <section id="contact" className="prose prose-slate max-w-none">
        <h2>16. Contact &amp; Controller</h2>
        <p>
          Controller: {CONTROLLER_NAME}
          {CONTROLLER_ADDRESS ? ` — ${CONTROLLER_ADDRESS}` : ""}.<br />
          Email:{" "}
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="underline decoration-dotted"
          >
            {CONTACT_EMAIL}
          </a>
          <br />
          Website:{" "}
          <a
            href={SITE_URL}
            className="underline decoration-dotted"
            target="_blank"
            rel="noreferrer"
          >
            {SITE_URL}
          </a>
        </p>
      </section>

      <section id="terms" className="prose prose-slate max-w-none">
        <h2>17. Terms of Use (EULA)</h2>
        <p>
          By using our apps and website, you agree to our Terms of Use (EULA).
          You can review the Terms here:{" "}
          <a
            href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
            className="underline decoration-dotted"
            target="_blank"
            rel="noreferrer"
          >
            Apple Standard EULA
          </a>
          . If you publish a custom EULA, replace this link and also add it in
          App Store Connect.
        </p>
      </section>
    </main>
  );
}

export default Privacy;
